import { tField, tStatus } from "../translations";
import type { Doc, Id } from "../../../../../convex/_generated/dataModel";

export type HumanTaskKind =
  | "approval"
  | "outbound_approval"
  | "browser_handoff"
  | "clarification"
  | "destructive_confirm";

export interface DecisionItem {
  id: Id<"humanTasks">;          // typed strongly so component code needs no casts
  title: string;
  statusLabel: string;
  kind: HumanTaskKind | string;  // string allows graceful handling of future kinds
  canRespond: boolean;           // true only for kind === "clarification" and non-terminal
  canSnooze: boolean;            // true for any non-terminal row
  canResolve: boolean;           // true for any non-terminal row
  subtitle: string;
  createdAt: number;             // Unix milliseconds; from doc.createdAt or doc._creationTime
  relativeTime: string;          // humanized relative time string (snapshot, not live-ticking)
}

/** Human-readable title per deployed HumanTaskKind value. */
const KIND_TITLE: Record<string, string> = {
  approval:            tField("humanTask.kind.approval"),
  outbound_approval:   tField("humanTask.kind.outbound_approval"),
  browser_handoff:     tField("humanTask.kind.browser_handoff"),
  clarification:       tField("humanTask.kind.clarification"),
  destructive_confirm: tField("humanTask.kind.destructive_confirm"),
};

const FALLBACK_TITLE = "Decision";

/**
 * Converts a relative time offset in milliseconds to a human-readable string.
 * Examples: "just now", "3 minutes ago", "2 hours ago", "5 days ago".
 *
 * NOTE: This is a snapshot value computed at transform time, not a live-ticking
 * counter. A user keeping the tray open for an hour will see the same
 * "5 minutes ago" string until a subscription tick re-pushes the row. A
 * future HARDEN-mode hook layer could update this on a setInterval; this
 * document does not implement that.
 */
function humanizeRelativeTime(diffMs: number): string {
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

/**
 * Truncates a payload to a subtitle string of at most 80 characters.
 * Never throws regardless of payload shape.
 *
 * Cascade priority: payload.message → payload.description → payload.text → "".
 * For clarification payloads (typically { question: string }), the cascade
 * falls through to "" because "question" is not in the priority list. An
 * empty subtitle is acceptable UX for any kind. A future HARDEN-mode change
 * could add "question" to the cascade.
 */
function extractSubtitle(payload: unknown): string {
  if (payload == null) return "";
  if (typeof payload === "string") {
    return payload.length > 80 ? payload.slice(0, 77) + "..." : payload;
  }
  if (typeof payload === "object") {
    const p = payload as Record<string, unknown>;
    const candidate =
      String(p["message"] ?? p["description"] ?? p["text"] ?? "");
    return candidate.length > 80 ? candidate.slice(0, 77) + "..." : candidate;
  }
  return "";
}

/**
 * Pure transform: converts a raw humanTasks Convex document into a DecisionItem
 * suitable for direct use by screen components.
 *
 * Never throws. Degrades gracefully for unrecognized kind values, missing
 * createdAt fields, and non-object payloads.
 */
export function transformHumanTask(doc: Doc<"humanTasks">): DecisionItem {
  // Fall back to _creationTime if createdAt is absent (e.g., legacy migration
  // gap or a test fixture that seeded the row without createdAt).
  const createdAt = doc.createdAt ?? doc._creationTime;

  const now = Date.now();
  const relativeTime = humanizeRelativeTime(now - createdAt);

  const isTerminal =
    doc.status === "resolved" ||
    doc.status === "expired" ||
    doc.status === "cancelled";

  return {
    id:          doc._id,                                   // typed Id<"humanTasks"> directly
    title:       KIND_TITLE[doc.kind] ?? FALLBACK_TITLE,
    statusLabel: tStatus(`humanTask.status.${doc.status}`),
    kind:        doc.kind as HumanTaskKind,
    canRespond:  doc.kind === "clarification" && !isTerminal,
    canSnooze:   !isTerminal,
    canResolve:  !isTerminal,
    subtitle:    extractSubtitle(doc.payload),
    createdAt,
    relativeTime,
  };
}
